import {
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzDrawerRef, NZ_DRAWER_DATA } from 'ng-zorro-antd/drawer';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GetParams, PostResponse, ToastConfig } from '@app/shared';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  ActivityService,
  Activity,
  CreateActivityPayload,
  UpdateActivityPayload,
  ParentEntityType,
  PARENT_ENTITY_TYPE,
  CalendarLinkService,
  CalendarEventData,
  DrawerEscapeBlockerService,
} from '@app/core';
import { AuthService } from '../../../../core/services/auth.service';
import { GlobalValuesService } from '../../../../core/services/global-values.service';
import { finalize } from 'rxjs';

interface ActivityType {
  id: number;
  name: string;
  openCalendar?: boolean;
}

interface CommercialUser {
  id: number;
  name: string;
  initials: string;
  email?: string;
}

interface DrawerData {
  opportunityId?: number;
  leadId?: number;
  saleOrderId?: number; // ID del presupuesto (SaleOrder) cuando está en modo budget
  projectId?: number; // ID del proyecto cuando viene desde un proyecto
  parentEntityType?: number; // Tipo de entidad padre (opcional, para casos específicos)
  activity?: Activity; // Actividad a editar (modo edición)
  context?: 'activity-panel' | 'activities-feature'; // Contexto desde donde se abre el drawer
}

@Component({
  selector: 'oportunities-details-activity-type-drawer',
  standalone: false,
  templateUrl: './oportunities-details-activity-drawer.component.html',
  styleUrl: './oportunities-details-activity-drawer.component.less',
})
export class OportunitiesDetailsActivityDrawerComponent
  implements OnInit, OnDestroy
{
  private destroyRef = inject(DestroyRef);

  @Output() activityScheduled = new EventEmitter<any>();
  @Output() createEvent = new EventEmitter<void>();
  @Output() activityUpdated = new EventEmitter<any>();

  activityForm!: FormGroup;
  scheduling: boolean = false;
  loadingActivityTypes: boolean = false;

  // IDs de la oportunidad, lead, saleOrder o project
  opportunityId?: number;
  leadId?: number;
  saleOrderId?: number;
  projectId?: number;

  // ID del usuario logueado
  userId?: number;

  // Modo edición
  isEditMode: boolean = false;
  activityToEdit?: Activity;

  // Contexto desde donde se abre el drawer
  context: 'activity-panel' | 'activities-feature' = 'activity-panel';

  // Para el range picker con hora
  timezone: string =
    Intl.DateTimeFormat()
      .resolvedOptions()
      .timeZone.replace('_', '/')
      .substring(0, 15) + '...';

  // Tipos de actividad cargados del backend
  activityTypes: ActivityType[] = [];

  // Usuarios comerciales
  commercialUsers: CommercialUser[] = [];
  loadingUsers: boolean = false;

  // Flags para controlar cuándo cargar datos de edición
  private activityTypesLoaded: boolean = false;
  private usersLoaded: boolean = false;

  // Rangos rápidos para el date picker
  selectedQuickRange: string | null = null;
  dateRanges: { [key: string]: Date } = {};

  // Mostrar botón de calendario si el tipo de actividad lo requiere
  get showCalendarButton(): boolean {
    const selectedTypeId = this.activityForm?.get('activityTypeId')?.value;
    if (!selectedTypeId) return false;
    const selectedType = this.activityTypes.find(
      (t) => t.id === selectedTypeId,
    );
    return selectedType?.openCalendar === true;
  }

  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Actividad programada exitosamente.',
    showUndo: false,
  };

  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private fb: FormBuilder,
    private notificationService: NzNotificationService,
    private activityService: ActivityService,
    private authService: AuthService,
    private calendarLinkService: CalendarLinkService,
    private globalValuesService: GlobalValuesService,
    @Optional() @Inject(NZ_DRAWER_DATA) private drawerData: DrawerData,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) {
    // Obtener los IDs del drawer data
    if (this.drawerData) {
      this.opportunityId = this.drawerData.opportunityId;
      this.leadId = this.drawerData.leadId;
      this.saleOrderId = this.drawerData.saleOrderId;
      this.projectId = this.drawerData.projectId;
      this.context = this.drawerData.context || 'activity-panel';

      // Modo edición si viene una actividad
      if (this.drawerData.activity) {
        this.isEditMode = true;
        this.activityToEdit = this.drawerData.activity;
      }
    }

    // Obtener el userId del usuario logueado
    const loggedUser = this.authService.loggedUser$.value;
    if (loggedUser) {
      console.log('loggedUser', loggedUser);
      this.userId = loggedUser.id;
    }
  }

  ngOnInit(): void {
    this.activityForm = this.fb.group({
      activityTypeId: [null, [Validators.required]],
      dateDeadLine: [null, [Validators.required]],
      summary: [''],
      assignedUserId: [null],
      note: [''],
    });
    // AUTO-SETEAR EL USUARIO LOGUEADO
    const loggedUser = this.authService.loggedUser$.value;
    this.autoSetLoggedUser();
    // Cargar tipos de actividad del backend
    this.loadActivityTypes();

    // Cargar usuarios comerciales
    this.loadCommercialUsers();

    // Inicializar rangos rápidos para el date picker
    this.dateRanges = {
      'Last 3 Days': this.addDays(new Date(), -3),
      'Last 7 Days': this.addDays(new Date(), -7),
      'Last 14 Days': this.addDays(new Date(), -14),
      'Last Month': this.addDays(new Date(), -30),
    };

    this.drawerEscapeBlocker.block();
  }

  private autoSetLoggedUser(): void {
    const loggedUser = this.authService.loggedUser$.value;
    if (loggedUser) {
      const exists = this.commercialUsers.some(
        (u) => u.id === (loggedUser.partner?.id || loggedUser.id),
      );

      if (!exists) {
        this.commercialUsers.unshift({
          id: loggedUser.partner?.id || loggedUser.id,
          name: loggedUser.partner.name,
          initials: loggedUser.partner.name?.substring(0, 2).toUpperCase(),
        });
      }

      this.activityForm.patchValue({
        assignedUserId: loggedUser.partner?.id || loggedUser.id,
      });
    }
  }

  private tryLoadActivityData(): void {
    // Solo cargar cuando ambos (tipos de actividad y usuarios) estén listos
    if (this.activityTypesLoaded && this.usersLoaded) {
      this.loadActivityData();
    }
  }

  private loadActivityData(): void {
    if (!this.activityToEdit) return;

    // Preparar la fecha - usar Date simple, no array
    let dateDeadLine: Date | null = null;
    if (this.activityToEdit.dateDeadLine) {
      dateDeadLine = new Date(this.activityToEdit.dateDeadLine);
    }

    // Obtener el partner.id correspondiente al userId de la actividad
    // porque el select usa partner.id pero la actividad tiene user.id
    const userId = this.activityToEdit.userId;
    let assignedPartnerId: number | null = null;

    if (userId) {
      // Buscar el usuario en la lista y obtener su partner.id
      const users = this.globalValuesService.users$.value;
      const user = users.find((u) => u.id === userId);
      if (user?.partner?.id) {
        assignedPartnerId = user.partner.id;
      } else {
        // Si no se encuentra, usar el userId directamente como fallback
        // (en caso de que userId sea realmente un partnerId)
        assignedPartnerId = userId;
      }
    }

    this.activityForm.patchValue({
      activityTypeId: this.activityToEdit.activityTypeId,
      dateDeadLine: dateDeadLine,
      summary: this.htmlToText(this.activityToEdit.summary || ''),
      assignedUserId: assignedPartnerId,
      note: this.activityToEdit.note || '',
    });
  }

  /**
   * Convierte HTML a texto plano legible
   */
  private htmlToText(html: string): string {
    if (!html) return '';
    
    // Crear un elemento temporal para parsear el HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Extraer el texto sin etiquetas HTML y quitar saltos de línea al inicio y final
    const text = temp.textContent || temp.innerText || '';
    return text.trim();
  }

  private loadActivityTypes(): void {
    this.loadingActivityTypes = true;
    this.activityService
      .getActivityTypes({
        startRow: 0,
        endRow: 5000,
        sortDirection: 'asc',
        sortProperty: 'id',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          // Manejar diferentes estructuras de respuesta
          let rows: any[] = [];

          if (Array.isArray(response)) {
            rows = response;
          } else if (response.data?.rows) {
            rows = response.data.rows;
          } else if (Array.isArray(response.data)) {
            rows = response.data;
          }

          this.activityTypes = rows.map((row: any) => ({
            id: row.id,
            name: row.name || row.label || `Tipo ${row.id}`,
            openCalendar: row.openCalendar || false,
          }));

          this.activityTypesLoaded = true;
          this.loadingActivityTypes = false;

          // Si es modo edición, intentar cargar los datos cuando ambos estén listos
          if (this.isEditMode && this.activityToEdit) {
            this.tryLoadActivityData();
          } else if (this.activityTypes.length > 0) {
            // Solo seleccionar el primer tipo por defecto en modo creación
            this.activityForm.patchValue({
              activityTypeId: this.activityTypes[0].id,
            });
          }
        },
        error: (e) => {
          console.error('Error al cargar tipos de actividad:', e);
          this.loadingActivityTypes = false;
        },
      });
  }

  private loadCommercialUsers(): void {
    this.loadingUsers = true;

    // Usar la lista global de usuarios (cada uno trae su partner)
    const users = this.globalValuesService.users$.value || [];

    // Mapear al formato CommercialUser pero manteniendo el id = partner.id
    this.commercialUsers = users
      .filter((u) => u.partner?.id)
      .map((u) => {
        const partner = u.partner;
        const name =
          `${partner.name || ''} ${partner.surName || ''}`.trim() ||
          `Partner ${partner.id}`;
        const initials = this.getInitials(name);

        return {
          id: partner.id, // el select sigue usando partner.id
          name,
          initials,
          email: u.email || partner.email,
        };
      });

    this.usersLoaded = true;
    this.loadingUsers = false;

    // Si es modo edición, intentar cargar los datos cuando ambos estén listos
    if (this.isEditMode && this.activityToEdit) {
      this.tryLoadActivityData();
    } else {
      // Seleccionar el usuario logueado por defecto si está en la lista (solo en modo creación)
      const defaultPartnerId =
        this.authService.loggedUser$.value?.partner?.id ||
        (this.commercialUsers.length > 0 ? this.commercialUsers[0].id : null);
      if (defaultPartnerId) {
        this.activityForm.patchValue({
          assignedUserId: defaultPartnerId,
        });
      }
    }
  }

  private getInitials(name: string): string {
    if (!name) return '??';
    const parts = name.split(' ').filter((p) => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  scheduleActivity(): void {
    if (this.activityForm.valid) {
      this.scheduling = true;

      if (this.isEditMode) {
        this.updateActivity();
      } else {
        this.createActivity();
      }
    } else {
      console.log('Formulario inválido, revisa los errores');
      this.activityForm.markAllAsTouched();
    }
  }

  private createActivity(): void {
    const payload = this.getPayload();

    this.activityService
      .createActivity(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.scheduling = false;
        }),
      )
      .subscribe({
        next: (rsp: PostResponse) => {
          if (rsp.statusCode === 200 || rsp.statusCode === 201) {
            this.activityScheduled.emit(rsp.data);
            this.createEvent.emit();
            this.close();
            this.showNotification();
          } else {
            this.showErrorNotification('Error al programar la actividad.');
          }
        },
        error: (e) => {
          console.error('Error al crear actividad:', e);
          this.showErrorNotification(
            e.error?.message || 'Error al programar la actividad.',
          );
        },
      });
  }

  private updateActivity(): void {
    const payload = this.getUpdatePayload();

    this.activityService
      .updateActivity(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.scheduling = false;
        }),
      )
      .subscribe({
        next: (rsp: PostResponse) => {
          if (rsp.statusCode === 200 || rsp.statusCode === 201) {
            this.activityUpdated.emit(rsp.data);
            this.createEvent.emit();
            this.close();
            // Solo mostrar notificación si NO estamos en activities-feature
            // (activities-feature maneja sus propias notificaciones)
            if (this.context !== 'activities-feature') {
              this.showNotification('Actividad actualizada.');
            }
          } else {
            this.showErrorNotification('Error al actualizar la actividad.');
          }
        },
        error: (e) => {
          console.error('Error al actualizar actividad:', e);
          this.showErrorNotification(
            e.error?.message || 'Error al actualizar la actividad.',
          );
        },
      });
  }

  scheduleAndNext(): void {
    if (this.activityForm.valid) {
      this.scheduling = true;

      const payload = this.getPayload();

      this.activityService
        .createActivity(payload)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.scheduling = false;
          }),
        )
        .subscribe({
          next: (rsp: PostResponse) => {
            if (rsp.statusCode === 200 || rsp.statusCode === 201) {
              this.activityScheduled.emit(rsp.data);
              // NO emitir createEvent para que el drawer no se cierre
              this.resetForm();
              this.autoSetLoggedUser();
              this.showNotification();
            } else {
              this.showErrorNotification('Error al programar la actividad.');
            }
          },
          error: (e) => {
            console.error('Error al crear actividad:', e);
            this.showErrorNotification(
              e.error?.message || 'Error al programar la actividad.',
            );
          },
        });
    } else {
      console.log('Formulario inválido, revisa los errores');
      this.activityForm.markAllAsTouched();
    }
  }

  private getPayload(): CreateActivityPayload & { dateCreated?: string } {
    const { parentEntityType, parentEntityId } = this.getParentEntity();
    const dateDeadLine = this.getDateDeadLine();
    const dateCreated = this.getDateStart(); // Primera fecha del range picker = fecha de creación

    // Convertir el partner.id seleccionado al user.id correspondiente
    // porque el formulario usa partners pero el backend espera user.id
    const selectedPartnerId = this.activityForm.value.assignedUserId;
    let userId = this.userId || 1;

    if (selectedPartnerId) {
      // Buscar TODOS los usuarios que tienen este partner.id
      // (en caso de que haya múltiples usuarios con el mismo partnerId)
      const users = this.globalValuesService.users$.value;
      const matchingUsers = users.filter(
        (u) => u.partner?.id === selectedPartnerId,
      );

      if (matchingUsers.length === 0) {
        // CASO EDGE MUY ESPECÍFICO: No se encuentra ningún usuario con ese partner.id
        // Esto puede pasar si hay un desfase entre la lista de partners y usuarios
        console.warn(
          `⚠️ ADVERTENCIA: No se encontró ningún usuario con partner.id = ${selectedPartnerId}. ` +
            `Se usará el usuario logueado (userId: ${
              this.userId || 1
            }) como fallback. ` +
            `El backend debe revisar esta inconsistencia.`,
        );
        // Usar el usuario logueado en lugar del partnerId directamente
        // (es más seguro que usar el partnerId como userId)
        userId = this.userId || 1;
      } else if (matchingUsers.length === 1) {
        // Caso ideal: solo un usuario con ese partnerId
        userId = matchingUsers[0].id;
      } else {
        // CASO EDGE: múltiples usuarios con el mismo partnerId
        // Priorizar el usuario logueado si está en la lista
        const loggedUser = matchingUsers.find((u) => u.id === this.userId);
        if (loggedUser) {
          userId = loggedUser.id;
          console.warn(
            `⚠️ ADVERTENCIA: Múltiples usuarios (${matchingUsers.length}) comparten el partner.id = ${selectedPartnerId}. ` +
              `Se seleccionó el usuario logueado (userId: ${userId}). ` +
              `Usuarios encontrados: ${matchingUsers
                .map((u) => `userId:${u.id}`)
                .join(', ')}`,
          );
        } else {
          // Si el usuario logueado no está, usar el primero (menor ID como criterio)
          userId = matchingUsers.sort((a, b) => a.id - b.id)[0].id;
          console.warn(
            `⚠️ ADVERTENCIA: Múltiples usuarios (${matchingUsers.length}) comparten el partner.id = ${selectedPartnerId}. ` +
              `Se seleccionó el primero (userId: ${userId}). ` +
              `Usuarios encontrados: ${matchingUsers
                .map((u) => `userId:${u.id}`)
                .join(', ')}. ` +
              `El backend debe corregir esta inconsistencia.`,
          );
        }
      }
    }

    return {
      activityTypeId: this.activityForm.value.activityTypeId,
      parentEntityType: parentEntityType,
      parentEntityId: parentEntityId,
      userId: userId,
      dateDeadLine: dateDeadLine,
      dateCreated: dateCreated, // Primera fecha del range picker = fecha de creación
      summary: this.activityForm.value.summary || '',
      note: this.activityForm.value.note || '',
      state: 'Planned',
    };
  }

  private getUpdatePayload(): Omit<
    UpdateActivityPayload,
    'parentEntityType'
  > & { dateCreated?: string; parentEntityType: number | string } {
    // En modo edición desde proyectos, usar SIEMPRE el contexto actual para asegurar consistencia
    // Esto corrige actividades que puedan tener valores incorrectos guardados
    const parentEntity = this.getParentEntity();
    const parentEntityType: number | string = parentEntity.parentEntityType;
    const parentEntityId: number = parentEntity.parentEntityId;

    // Log para debugging
    console.log(
      `📝 Editando actividad ${this.activityToEdit?.id} con contexto actual:`,
      {
        original: {
          type: this.activityToEdit?.parentEntityType,
          id: this.activityToEdit?.parentEntityId,
        },
        usando: { type: parentEntityType, id: parentEntityId },
      },
    );

    const dateDeadLine = this.getDateDeadLine(); // Segunda fecha del range (fecha de vencimiento)
    const dateCreated = this.getDateStart(); // Primera fecha del range (fecha de creación)

    // Convertir el partner.id seleccionado al user.id correspondiente
    // porque el formulario usa partners pero el backend espera user.id
    const selectedPartnerId = this.activityForm.value.assignedUserId;
    let userId = this.activityToEdit?.userId || this.userId || 1;

    if (selectedPartnerId) {
      // Buscar TODOS los usuarios que tienen este partner.id
      // (en caso de que haya múltiples usuarios con el mismo partnerId)
      const users = this.globalValuesService.users$.value;
      const matchingUsers = users.filter(
        (u) => u.partner?.id === selectedPartnerId,
      );

      if (matchingUsers.length === 0) {
        // CASO EDGE MUY ESPECÍFICO: No se encuentra ningún usuario con ese partner.id
        // Esto puede pasar si hay un desfase entre la lista de partners y usuarios
        console.warn(
          `⚠️ ADVERTENCIA: No se encontró ningún usuario con partner.id = ${selectedPartnerId}. ` +
            `Se mantendrá el usuario original de la actividad (userId: ${
              this.activityToEdit?.userId || this.userId || 1
            }) como fallback. ` +
            `El backend debe revisar esta inconsistencia.`,
        );
        // Mantener el usuario original o el logueado (no usar partnerId como userId)
        userId = this.activityToEdit?.userId || this.userId || 1;
      } else if (matchingUsers.length === 1) {
        // Caso ideal: solo un usuario con ese partnerId
        userId = matchingUsers[0].id;
      } else {
        // CASO EDGE: múltiples usuarios con el mismo partnerId
        // En modo edición: priorizar el usuario que ya estaba asignado a la actividad
        const existingUser = matchingUsers.find(
          (u) => u.id === this.activityToEdit?.userId,
        );
        if (existingUser) {
          userId = existingUser.id;
          console.warn(
            `⚠️ ADVERTENCIA: Múltiples usuarios (${matchingUsers.length}) comparten el partner.id = ${selectedPartnerId}. ` +
              `Se mantuvo el usuario original (userId: ${userId}). ` +
              `Usuarios encontrados: ${matchingUsers
                .map((u) => `userId:${u.id}`)
                .join(', ')}`,
          );
        } else {
          // Si no está el usuario original, priorizar el usuario logueado
          const loggedUser = matchingUsers.find((u) => u.id === this.userId);
          if (loggedUser) {
            userId = loggedUser.id;
            console.warn(
              `⚠️ ADVERTENCIA: Múltiples usuarios (${matchingUsers.length}) comparten el partner.id = ${selectedPartnerId}. ` +
                `Se seleccionó el usuario logueado (userId: ${userId}). ` +
                `Usuarios encontrados: ${matchingUsers
                  .map((u) => `userId:${u.id}`)
                  .join(', ')}`,
            );
          } else {
            // Si el usuario logueado tampoco está, usar el primero (menor ID como criterio)
            userId = matchingUsers.sort((a, b) => a.id - b.id)[0].id;
            console.warn(
              `⚠️ ADVERTENCIA: Múltiples usuarios (${matchingUsers.length}) comparten el partner.id = ${selectedPartnerId}. ` +
                `Se seleccionó el primero (userId: ${userId}). ` +
                `Usuarios encontrados: ${matchingUsers
                  .map((u) => `userId:${u.id}`)
                  .join(', ')}. ` +
                `El backend debe corregir esta inconsistencia.`,
            );
          }
        }
      }
    }

    return {
      id: this.activityToEdit?.id || 0,
      activityTypeId: this.activityForm.value.activityTypeId,
      parentEntityType: parentEntityType as number | string,
      parentEntityId: parentEntityId as number,
      userId: userId,
      date:
        dateCreated ||
        this.activityToEdit?.dateCreated ||
        new Date().toISOString(),
      dateCreated: dateCreated || this.activityToEdit?.dateCreated, // Primera fecha del range = fecha de creación
      summary: this.activityForm.value.summary || '',
      note: this.activityForm.value.note || '',
      dateDeadLine: dateDeadLine, // Segunda fecha del range = fecha de vencimiento
      state: this.activityToEdit?.state || 'Planned',
      dateDone: this.activityToEdit?.dateDone,
    };
  }

  private getParentEntity(): {
    parentEntityType: number | string;
    parentEntityId: number;
  } {
    let parentEntityType: number | string;
    let parentEntityId: number;

    // Si estamos en modo edición desde activities-feature, usar los valores de la actividad
    if (this.context === 'activities-feature' && this.isEditMode && this.activityToEdit) {
      // Convertir el parentEntityType string a su equivalente numérico
      const typeFromActivity = this.activityToEdit.parentEntityType;
      if (typeof typeFromActivity === 'string') {
        // Mapear strings a sus valores numéricos
        const typeMap: { [key: string]: number } = {
          'Lead': PARENT_ENTITY_TYPE.LEAD, // 49
          'Opportunity': PARENT_ENTITY_TYPE.OPPORTUNITY, // 49
          'Project': PARENT_ENTITY_TYPE.PROJECT, // 5
          'SaleOrder': PARENT_ENTITY_TYPE.SALE_ORDER, // 51
        };
        parentEntityType = typeMap[typeFromActivity] || PARENT_ENTITY_TYPE.NONE;
      } else {
        parentEntityType = typeFromActivity || PARENT_ENTITY_TYPE.NONE;
      }
      parentEntityId = this.activityToEdit.parentEntityId || 0;
    } else if (this.saleOrderId) {
      // SaleOrder debe usar el nombre como string 'SaleOrder' en lugar del número
      parentEntityType = 'SaleOrder';
      parentEntityId = this.saleOrderId;
    } else if (this.projectId) {
      // Project usa parentEntityType: 5
      parentEntityType = PARENT_ENTITY_TYPE.PROJECT; // 5
      parentEntityId = this.projectId;
    } else if (this.leadId) {
      // Lead usa parentEntityType: 49
      parentEntityType = PARENT_ENTITY_TYPE.LEAD; // 49
      parentEntityId = this.leadId;
    } else if (this.opportunityId) {
      // Opportunity también usa parentEntityType: 49
      parentEntityType = PARENT_ENTITY_TYPE.OPPORTUNITY; // 49
      parentEntityId = this.opportunityId;
    } else {
      parentEntityType = PARENT_ENTITY_TYPE.NONE;
      parentEntityId = 0;
    }

    return { parentEntityType, parentEntityId };
  }

  /**
   * Obtiene la primera fecha del range picker (fecha de inicio/creación)
   */
  private getDateStart(): string | undefined {
    const dateValue = this.activityForm.value.dateDeadLine;
    let dateStart: string | undefined;

    if (Array.isArray(dateValue) && dateValue[0]) {
      // La primera fecha del range es la fecha de inicio/creación
      dateStart = dateValue[0].toISOString();
    } else if (dateValue instanceof Date) {
      dateStart = dateValue.toISOString();
    } else if (dateValue) {
      const date = new Date(dateValue);
      dateStart = date.toISOString();
    }

    return dateStart;
  }

  private getDateDeadLine(): string | undefined {
    const dateValue = this.activityForm.value.dateDeadLine;
    let dateDeadLine: string | undefined;

    if (Array.isArray(dateValue) && dateValue[0]) {
      // Para actividades, la fecha de vencimiento es la PRIMERA fecha del rango
      dateDeadLine = dateValue[0].toISOString();
    } else if (dateValue instanceof Date) {
      dateDeadLine = dateValue.toISOString();
    } else if (dateValue) {
      dateDeadLine = new Date(dateValue).toISOString();
    }

    return dateDeadLine;
  }

  /**
   * Obtiene la fecha de inicio y fecha límite del rango seleccionado
   * @returns Objeto con dateStart (fecha inicio) y dateDeadLine (fecha fin)
   */
  private getDateRange(): { dateStart?: string; dateDeadLine?: string } {
    const dateValue = this.activityForm.value.dateDeadLine;
    let dateStart: string | undefined;
    let dateDeadLine: string | undefined;

    if (Array.isArray(dateValue)) {
      // Si es un array, usar la primera fecha tanto para inicio como para fecha límite
      if (dateValue[0]) {
        dateStart = dateValue[0].toISOString();
        dateDeadLine = dateValue[0].toISOString();
      }
    } else if (dateValue instanceof Date) {
      // Si es una sola fecha, usarla como fecha límite
      dateDeadLine = dateValue.toISOString();
      dateStart = dateValue.toISOString();
    } else if (dateValue) {
      // Si es un string u otro formato, convertirlo
      const date = new Date(dateValue);
      dateDeadLine = date.toISOString();
      dateStart = date.toISOString();
    }

    return { dateStart, dateDeadLine };
  }

  private resetForm(): void {
    const defaultActivityTypeId =
      this.activityTypes.length > 0 ? this.activityTypes[0].id : null;

    // Usar el usuario logueado o el primer usuario de la lista como defecto
    const defaultUserId =
      this.userId ||
      (this.commercialUsers.length > 0 ? this.commercialUsers[0].id : null);

    // Resetear formulario con valores por defecto
    this.activityForm.reset({
      activityTypeId: defaultActivityTypeId,
      dateDeadLine: null,
      summary: '',
      assignedUserId: defaultUserId,
      note: '',
    });
  }

  private showNotification(
    message: string = 'Actividad programada exitosamente.',
  ): void {
    this.toastConfig = {
      icon: 'check_circle',
      iconColor: '#6CDA75',
      content: message,
      showUndo: false,
    };
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' },
    );
  }

  private showErrorNotification(message: string): void {
    this.toastConfig = {
      icon: 'dangerous',
      iconColor: '#CB2A2F',
      content: message,
      showUndo: false,
    };
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' },
    );
  }

  close(): void {
    this.resetForm();
    this.drawerRef.close();
  }

  /**
   * Abre Google Calendar con el evento prellenado (sin OAuth)
   */
  openGoogleCalendar(): void {
    const eventData = this.buildCalendarEventData();
    if (eventData) {
      this.calendarLinkService.openGoogleCalendar(eventData);
    }
  }

  /**
   * Descarga archivo .ics compatible con cualquier calendario
   */
  downloadIcsFile(): void {
    const eventData = this.buildCalendarEventData();
    if (eventData) {
      this.calendarLinkService.downloadIcsFile(eventData);
      this.showNotification('Archivo .ics descargado');
    }
  }

  /**
   * Abre Outlook Calendar con el evento prellenado
   */
  openOutlookCalendar(): void {
    const eventData = this.buildCalendarEventData();
    if (eventData) {
      this.calendarLinkService.openOutlookCalendar(eventData);
    }
  }

  /**
   * Construye los datos del evento para el calendario
   */
  private buildCalendarEventData(): CalendarEventData | null {
    const formValue = this.activityForm.value;

    // Obtener fecha de inicio
    let startDate: Date;
    if (Array.isArray(formValue.dateDeadLine) && formValue.dateDeadLine[0]) {
      startDate = new Date(formValue.dateDeadLine[0]);
    } else if (formValue.dateDeadLine) {
      startDate = new Date(formValue.dateDeadLine);
    } else {
      startDate = new Date();
    }

    // Obtener fecha de fin (1 hora después por defecto)
    let endDate: Date;
    if (Array.isArray(formValue.dateDeadLine) && formValue.dateDeadLine[1]) {
      endDate = new Date(formValue.dateDeadLine[1]);
    } else {
      endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    }

    const title = formValue.summary || 'Reunión - MojitoCRM';
    const description = formValue.note || '';

    return {
      title,
      description,
      startDate,
      endDate,
    };
  }

  /**
   * Método legacy - redirige a openGoogleCalendar
   */
  openCalendar(): void {
    this.openGoogleCalendar();
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  onQuickRangeChange(value: string): void {
    if (!value) return;

    let date: Date | null = null;

    switch (value) {
      case 'last3days':
        date = this.addDays(new Date(), -3);
        break;
      case 'last7days':
        date = this.addDays(new Date(), -7);
        break;
      case 'last14days':
        date = this.addDays(new Date(), -14);
        break;
      case 'lastMonth':
        date = this.addDays(new Date(), -30);
        break;
    }

    if (date) {
      this.selectedQuickRange = value;
      this.activityForm.patchValue({
        dateDeadLine: date,
      });
    }
  }

  ngOnDestroy(): void {
    this.drawerEscapeBlocker.unblock();
  }
}
